import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import queryString from 'query-string';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useSocialProfilePermissions from '@/utils/hooks/swr/useSocialProfilePermissions';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';
import useStateWithReset from '@/utils/hooks/utils/useStateWithReset';

// Lib
import { FACEBOOK, INSTAGRAM, TIKTOK, TIKTOK_BUSINESS } from '@/lib/socialProfile';
import { OAUTH_NETWORKS } from '@/lib/routes/privateRoutes';
import { PERMISSIONS } from '@/lib/permissions/socialPermissions';
import { url_faq_can_see_analytics_ig_promoted_posts } from '@/lib/urls/faqUrls';

// Utils
import faqUrl from '@/utils/urls/faqUrl';
import { capitalizeString } from '@/utils/string/capitalizeString';

// Components
import LinkBase from '@/components/base/LinkBase';
import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';

// Types
import { SocialProfileType } from '@/types/socialProfileType';

const COOKIE_ADS_PERMISSIONS = 'ads_permissions';

const allowedSocialNetworks = [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, TIKTOK];

const AdsPermissionsBanner = ({ className }: { className: string }) => {
    const { activeSocialProfile } = useActiveSocialProfile();

    return (
        <>
            {allowedSocialNetworks.includes(activeSocialProfile?.social_network_type) ? (
                <AdsPermissionsUnlockBanner
                    socialProfile={activeSocialProfile}
                    className={className}
                />
            ) : null}
            <AdsPermissionsSuccessBanner className={className} />
            <AdsPermissionsErrorBanner className={className} />
        </>
    );
};

const AdsPermissionsUnlockBanner = ({
    socialProfile,
    className
}: {
    socialProfile: SocialProfileType;
    className: string;
}) => {
    const cookieName = `${COOKIE_ADS_PERMISSIONS}_${socialProfile.social_network_type}`;
    const hasClosedAdsPermission = Boolean(Cookies.get(cookieName));
    const [isOpen, setIsOpen] = useState(!hasClosedAdsPermission);

    const { t } = useTranslation('commons');
    const {
        permissions,
        permissionsError,
        isLoading: permissionsLoading
    } = useSocialProfilePermissions(socialProfile?.id);

    const { deleteAllQueries } = useRouterQuery();

    const getOauthUrl = () => {
        const redirectUrl = `${deleteAllQueries()}?${queryString.stringify({
            new_permission: PERMISSIONS.ADS
        })}`;

        let network: keyof typeof OAUTH_NETWORKS = socialProfile?.social_network_type;

        if ([TIKTOK, TIKTOK_BUSINESS].includes(network)) {
            network = 'tiktok_business_ads';
        }

        return `${OAUTH_NETWORKS[network]}?${queryString.stringify(
            {
                redirect_to: encodeURIComponent(redirectUrl),
                permissions: [PERMISSIONS.ADS]
            },
            { arrayFormat: 'bracket' }
        )}`;
    };

    useEffect(() => {
        setIsOpen(!hasClosedAdsPermission);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cookieName]);

    if (
        !isOpen ||
        permissionsLoading ||
        permissionsError ||
        permissions.includes(PERMISSIONS.ADS)
    ) {
        return null;
    }

    const getNetwork = () => {
        let network = socialProfile.social_network_type;

        if (network === TIKTOK_BUSINESS) {
            network = TIKTOK;
        }

        return capitalizeString(network);
    };

    const getToken = () => {
        const network = socialProfile.social_network_type;

        if (network === TIKTOK_BUSINESS || network === TIKTOK) {
            return 'commons:permissions_banners.ads.unlock.tiktok_business.message';
        }
        return 'commons:permissions_banners.ads.unlock.message';
    };

    return (
        <CompoundBanner className={className} type="info">
            <CompoundBanner.Icon icon="interface-paid-locked" />
            <CompoundBanner.Content title={t('permissions_banners.ads.unlock.title')}>
                <p>
                    <Trans
                        i18nKey={getToken()}
                        components={[
                            <LinkBase
                                key="link"
                                href={getOauthUrl()}
                                className="font-GSemiBold underline"
                            />,
                            <span key="social_profile" className="capitalize"></span>
                        ]}
                        values={{
                            social_profile: getNetwork()
                        }}
                    />
                </p>
            </CompoundBanner.Content>
            <CompoundBanner.Close
                onClose={() => {
                    Cookies.set(cookieName, '1', { expires: 30 });
                    setIsOpen(false);
                }}
            />
        </CompoundBanner>
    );
};

const AdsPermissionsSuccessBanner = ({ className }: { className: string }) => {
    const router = useRouter();
    const { deleteQuery } = useRouterQuery();
    const { new_permission, oauth_error } = router.query;
    const [hasNewAdsPermission, setHasNewAdsPermission] = useState(
        new_permission && new_permission === PERMISSIONS.ADS && !oauth_error
    );

    if (!hasNewAdsPermission) return null;

    return (
        <CompoundBanner className={className} type="success">
            <CompoundBanner.Icon icon="check" />
            <CompoundBanner.Content>
                <p>
                    <Trans
                        i18nKey={`commons:permissions_banners.ads.congrats`}
                        components={[
                            <LinkBase
                                key="link"
                                href={faqUrl(url_faq_can_see_analytics_ig_promoted_posts)}
                                className="font-GSemiBold underline"
                                target="_blank"
                                rel="help noopener noreferrer"
                            />
                        ]}
                    />
                </p>
            </CompoundBanner.Content>
            <CompoundBanner.Close
                onClose={() => {
                    setHasNewAdsPermission(false);
                    const newUrl = deleteQuery(['new_permission']);
                    router.replace(newUrl);
                }}
            />
        </CompoundBanner>
    );
};

const AdsPermissionsErrorBanner = ({ className }: { className: string }) => {
    const { t } = useTranslation('commons');
    const router = useRouter();
    const { new_permission, oauth_error } = router.query;
    const { clearUrlQueriesWithoutReload } = useRouterQuery();

    const [hasNewAdsPermissionError, setHasNewAdsPermissionError, reset] =
        useStateWithReset<string>(null);

    const getErrorMessage = (errorCode: string) => {
        if (errorCode === '2') {
            return 'permissions_banners.ads.tiktok_business_error';
        }

        return 'permissions_banners.ads.error';
    };

    useEffect(() => {
        if (new_permission && new_permission === PERMISSIONS.ADS && oauth_error) {
            setHasNewAdsPermissionError(getErrorMessage(oauth_error as string));
            clearUrlQueriesWithoutReload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [new_permission, oauth_error]);

    if (!hasNewAdsPermissionError) return null;

    return (
        <CompoundBanner type="error" className={className}>
            <CompoundBanner.Icon />
            <CompoundBanner.Content
                title={t('errors:error')}
                message={t(hasNewAdsPermissionError)}
            />
            <CompoundBanner.Close onClose={reset} />
        </CompoundBanner>
    );
};

export default AdsPermissionsBanner;
